/**
 * Office selector state (ADR-0012), pure parts.
 *
 * Source of truth while the app runs: the `office` URL search param
 * (`?office=all` or `?office=<uuid>`), so links and the back button carry
 * the view. Remembered per user on the server (preferences) and per user
 * and device in local storage, so the next visit opens where they left off.
 */
import type { LocationScope, OfficeScopeMode } from '@velora/contracts';
import type { ShellOffice } from '@velora/contracts/shell';
import { chooseInitialScope, scopeFromParam, scopeToParam } from '@velora/domain/tenancy';

export const OFFICE_PARAM = 'office';

export interface InitialScopeInput {
  urlParam: string | null | undefined;
  /**
   * The person's office selection held by identity (`/v1/identity/context`):
   * the office picked at sign-in, and every change since (the shell keeps it
   * in step). Optional so tests and demo boots can omit it.
   */
  personPref?: string | null | undefined;
  serverPref: string | null | undefined;
  localPref: string | null | undefined;
  offices: readonly ShellOffice[];
  defaultLocationId: string | null;
}

/**
 * Picks the scope to show: the URL if valid for this user, else the office
 * picked at sign-in (identity context), else the shell's server preference,
 * else this device's, else the default office, else the first.
 * Returns `null` when the user has no offices (they can see nothing).
 */
export function initialScope(input: InitialScopeInput): LocationScope | null {
  const ids = input.offices.map((o) => o.id);
  for (const candidate of [input.urlParam, input.personPref, input.serverPref, input.localPref]) {
    const parsed = scopeFromParam(candidate);
    if (!parsed) continue;
    const chosen = chooseInitialScope(parsed, ids, null);
    // chooseInitialScope falls back to the first office when the candidate
    // is invalid; only accept the candidate itself here.
    if (chosen && scopeToParam(chosen) === scopeToParam(parsed)) return chosen;
  }
  return chooseInitialScope(null, ids, input.defaultLocationId);
}

/**
 * What a route can show for the selected scope. A `location` route (for
 * example the schedule grid) cannot show "All offices": the shell asks the
 * user to pick one instead of guessing.
 */
export function scopeForRoute(
  scope: LocationScope,
  mode: OfficeScopeMode,
): { ok: true; scope: LocationScope } | { ok: false; reason: 'office_required' } {
  if (mode === 'location' && scope.kind === 'all') return { ok: false, reason: 'office_required' };
  return { ok: true, scope };
}

export function locationIdsFor(scope: LocationScope, offices: readonly ShellOffice[]): string[] {
  if (scope.kind === 'all') return offices.map((o) => o.id);
  return offices.some((o) => o.id === scope.locationId) ? [scope.locationId] : [];
}

export function scopeLabel(scope: LocationScope, offices: readonly ShellOffice[]): string {
  if (scope.kind === 'all') {
    return offices.length === 1
      ? (offices[0]?.name ?? 'Your office')
      : `All ${offices.length} offices`;
  }
  return offices.find((o) => o.id === scope.locationId)?.name ?? 'Unknown office';
}

export function localScopeKey(practiceId: string, userId: string): string {
  return `velora.office.${practiceId}.${userId}`;
}

export { scopeFromParam, scopeToParam };
